<template>
  <v-card class="mx-auto">
    <v-card-title class="text-h5 primary white--text">
      <v-icon color="white" class="mr-2">mdi-webhook</v-icon>
      Detalhes do Webhook
      <v-spacer></v-spacer>
      <v-btn icon color="white" @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text v-if="!canList" class="text-center py-8">
      <div class="red--text text-h6">
        <v-icon color="red" large class="mb-4">mdi-lock</v-icon>
        <br>
        Você não tem permissão para visualizar as informações desta tela
      </div>
    </v-card-text>

    <v-card-text v-if="canList" class="pt-4">
      <v-container>
        <!-- Informações Básicas -->
        <v-row>
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Informações Básicas
            </v-subheader>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="Nome"
              :value="item.nome"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Public ID"
              :value="item.public_id"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-text-field
              label="URL"
              :value="item.url"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              label="Descrição"
              :value="item.descricao"
              readonly
              outlined
              dense
              rows="3"
            ></v-textarea>
          </v-col>
        </v-row>

        <!-- Configurações -->
        <v-row>
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Configurações
            </v-subheader>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <v-chip
              :color="getMethodColor(item.http_Method)"
              text-color="white"
              class="ma-2"
            >
              <v-icon left>mdi-web</v-icon>
              {{ item.http_Method }}
            </v-chip>
          </v-col>
          <v-col cols="12" md="4">
            <v-chip
              :color="item.type === 'AUTH' ? 'info' : 'warning'"
              text-color="white"
              class="ma-2"
            >
              <v-icon left>mdi-tag</v-icon>
              {{ item.type }}
            </v-chip>
          </v-col>
          <v-col cols="12" md="4">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              text-color="white"
              class="ma-2"
            >
              <v-icon left>mdi-power</v-icon>
              {{ item.active ? 'Ativo' : 'Inativo' }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Headers e Dados -->
        <v-row>
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Headers e Dados
            </v-subheader>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="12">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Headers</v-card-title>
              <v-card-text>
                <pre v-if="item.headers" class="code-block">{{ formatJson(item.headers) }}</pre>
                <v-alert v-else type="info" dense text>
                  Nenhum header configurado
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Body</v-card-title>
              <v-card-text>
                <pre v-if="item.body" class="code-block">{{ formatJson(item.body) }}</pre>
                <v-alert v-else type="info" dense text>
                  Nenhum header configurado
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Response</v-card-title>
              <v-card-text>
                <pre v-if="item.response" class="code-block">{{ formatJson(item.response) }}</pre>
                <v-alert v-else type="info" dense text>
                  Nenhum payload configurado
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Eventos -->
        <v-row v-if="item.events && item.events.length">
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Eventos
            </v-subheader>
          </v-col>
        </v-row>

        <v-row v-if="item.events && item.events.length">
          <v-col cols="12">
            <v-chip
              v-for="event in item.events"
              :key="event"
              class="ma-1"
              color="secondary"
              text-color="white"
            >
              <v-icon left small>mdi-lightning-bolt</v-icon>
              {{ event }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Empresa/Gate Associado -->
        <v-row v-if="item.gate_detail">
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Empresa/Gate Associado
            </v-subheader>
          </v-col>
        </v-row>

        <v-row v-if="item.gate_detail">
          <v-col cols="12" md="6">
            <v-text-field
              label="Razão Social"
              :value="item.gate_detail.business_name"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Nome Fantasia"
              :value="item.gate_detail.trade_name"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="item.gate_detail">
          <v-col cols="12" md="4">
            <v-text-field
              label="CNPJ"
              :value="item.gate_detail.cnpj"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Telefone"
              :value="item.gate_detail.phone"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Cidade/UF"
              :value="`${item.gate_detail.city} - ${item.gate_detail.state}`"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Fornecedor -->
        <v-row v-if="item.fornecedor_detail">
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Fornecedor Associado
            </v-subheader>
          </v-col>
        </v-row>

        <v-row v-if="item.fornecedor_detail">
          <v-col cols="12" md="6">
            <v-text-field
              label="Razão Social"
              :value="item.fornecedor_detail.business_name"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Nome Fantasia"
              :value="item.fornecedor_detail.trade_name"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="item.fornecedor_detail">
          <v-col cols="12" md="4">
            <v-text-field
              label="CNPJ"
              :value="item.fornecedor_detail.cnpj"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Telefone"
              :value="item.fornecedor_detail.phone"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Cidade/UF"
              :value="`${item.fornecedor_detail.city} - ${item.fornecedor_detail.state}`"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Datas -->
        <v-row>
          <v-col cols="12">
            <v-subheader class="text-h6 font-weight-bold primary--text">
              Informações de Sistema
            </v-subheader>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="Criado em"
              :value="formatDate(item.created_at)"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="Atualizado em"
              :value="formatDate(item.updated_at)"
              readonly
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-card-actions v-if="canList">
      <v-spacer></v-spacer>
      <v-btn v-if="canUpdate" color="primary" @click="edit">
        <v-icon left>mdi-pencil</v-icon>
        Editar
      </v-btn>
      <v-btn color="grey" text @click="close">
        Fechar
      </v-btn>
    </v-card-actions>

    <v-card-actions v-if="!canList">
      <v-spacer></v-spacer>
      <v-btn color="grey" text @click="close">
        Fechar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { BasicPermissions, Sequences } from '@/utils/permissions'
import { toBrazilianDateTime } from '@/mixins/convertion'

export default {
  name: 'WebhookDetail',

  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    permission() {
      return Sequences.Webhooks.toString()
    },
    canList() {
      return this.$canDo(BasicPermissions.VISUALIZAR, this.permission)
    },
    canUpdate() {
      return this.$canDo(BasicPermissions.EDITAR, this.permission)
    }
  },

  methods: {
    close() {
      this.$emit('close')
    },

    edit() {
      this.$emit('edit', this.item)
    },

    formatJson(json) {
      if (!json) return ''
      if (typeof json === 'string') {
        try {
          return JSON.stringify(JSON.parse(json), null, 2)
        } catch {
          return json
        }
      }
      return JSON.stringify(json, null, 2)
    },

    formatDate(date) {
      if (!date) return ''
      return toBrazilianDateTime(date)
    },

    getMethodColor(method) {
      const colors = {
        'GET': 'success',
        'POST': 'info',
        'PUT': 'warning',
        'PATCH': 'orange',
        'DELETE': 'error'
      }
      return colors[method] || 'grey'
    }
  }
}
</script>

<style scoped>
.code-block {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  max-height: 200px;
  overflow-y: auto;
}

.v-subheader {
  padding-left: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
}
</style>